import React from 'react';
import { format } from 'date-fns';

import { mapToPluralForm } from '@crm-framework/utils';

import { LeadGridFormatterTemplateWithInject } from '@crm-lead/lead-short-info';
import { shortDateTimeFormat, shortDateTime } from '@crm-package/app';

import { NOTIFICATION_TYPES } from '../../consts';
import { getBalanceForDormantClientNotification } from '../../adapters';

import { INotificationMessageContentProps } from './types';

const ClientIdAndName: React.FC<{
  leadId: number | undefined;
  leadFullName?: string;
}> = ({ leadId, leadFullName, children }) =>
  leadId !== undefined ? (
    <>
      {`Client ID: `}
      <LeadGridFormatterTemplateWithInject leadId={leadId} />
      {`, Name: ${leadFullName}`}
      {children}
    </>
  ) : (
    <></>
  );

export const NotificationMessageContent: React.FC<
  INotificationMessageContentProps
> = ({ userId, notificationType, data }) => {
  const managerName =
    data.managerId && data.managerId !== userId
      ? `, Manager: ${data.managerName}`
      : '.';

  switch (notificationType) {
    case NOTIFICATION_TYPES.Deposit: {
      const cardNumber = data.cardNumber
        ? `*${data.cardNumber.replace(/\*/g, '').trim()}, `
        : '';

      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {`, Deposit via ${data.subType}, ${cardNumber} ${data.amount} ${data.currency}${managerName}`}
        </>
      );
    }
    case NOTIFICATION_TYPES.DepositAttempt:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {`, Deposit Attempt via ${data.subType}${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.WithdrawRequest:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {`, Withdraw ${data.amount} ${data.currency}${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.WithdrawRequestAttempt:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {`, Name: ${data.leadFullName}, Withdraw Request Attempts${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.CalendarEvent: {
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}>
            {' '}
          </ClientIdAndName>
          {`${data.eventName}`}
        </>
      );
    }
    case NOTIFICATION_TYPES.NewLeadsForSales:
      return (
        <>{`New Leads: ${data.newLeadsIds.length}, Without attempt: ${data.newLeadsWithoutAttemptIds.length}`}</>
      );
    case NOTIFICATION_TYPES.NewLeadsForTeamManagers:
      return (
        <>{`New Leads: ${data.newLeadsTotalCount}, Without attempt: ${data.newLeadsWithoutAttemptTotalCount}`}</>
      );
    case NOTIFICATION_TYPES.ClosePnlToBalanceLimitExceeded: {
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}>
            {' '}
          </ClientIdAndName>
          {` Close Pnl Deal: ${data.closePnlToBalancePercent}%${managerName}`}
        </>
      );
    }
    case NOTIFICATION_TYPES.CriticalEquityExceeded:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` Critical Equity = ${data.equityPercent}%${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.CriticalOpenPnlSumExceeded:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` Critical OpenPnl = ${data.openPnlPercent}%${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.DocumentUploaded:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` File name = ${data.documentName}`}
        </>
      );
    case NOTIFICATION_TYPES.LeadAction:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {`. Message: ${data.message}`}
        </>
      );
    case NOTIFICATION_TYPES.ExpiringDeals: {
      const { dealsCount, leadsCount } = data;
      const leadText = mapToPluralForm(leadsCount, 'client');
      const dealText = mapToPluralForm(dealsCount, 'deal');
      const dateFrom = format(new Date(data.dateFrom), shortDateTimeFormat);
      const dateTo = format(new Date(data.dateTo), shortDateTimeFormat);

      return (
        <>{`${dealText} of ${leadText} that expire from ${dateFrom} to ${dateTo}.`}</>
      );
    }
    case NOTIFICATION_TYPES.ExpiringDealsForTeamManagers: {
      const { dealsTotalCount, leadsTotalCount, managersTotalCount } = data;
      const leadTotalText = mapToPluralForm(leadsTotalCount, 'client');
      const dealTotalText = mapToPluralForm(dealsTotalCount, 'deal');
      const managerTotalText = mapToPluralForm(managersTotalCount, 'manager');
      const dateFrom = format(new Date(data.dateFrom), shortDateTimeFormat);
      const dateTo = format(new Date(data.dateTo), shortDateTimeFormat);

      return (
        <>{`${dealTotalText} of ${leadTotalText} that expire from ${dateFrom} to ${dateTo} (${managerTotalText}).`}</>
      );
    }
    case NOTIFICATION_TYPES.NewLeadDuplicateByPhone:
      return (
        <>
          <ClientIdAndName
            leadId={data.duplicateLeadId}
            leadFullName={data.duplicateLeadFullName}
          />
          {` is duplicate by phone of original `}
          <ClientIdAndName
            leadId={data.originalLeadId}
            leadFullName={data.originalLeadFullName}
          />
          .
        </>
      );
    case NOTIFICATION_TYPES.UnprocessedWithdrawRequest:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {`. Unprocessed withdrawal request ${data.id} from ${format(
            new Date(data.createdDate),
            shortDateTimeFormat
          )}.`}
        </>
      );
    case NOTIFICATION_TYPES.ClientChangedPersonalData:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` changed Personal Data${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.IdentityDocumentExpires:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` has ${data.documentType} that expires at ${format(
            new Date(data.expirationDate),
            shortDateTime
          )}${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.TotalDepositLimitExceeded:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` made ${data.depositAmountPbc} ${data.pbc} Total Deposit in the last ${data.days} days${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.NewLeadDepositCardUsed:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {`, New Card * ${data.cardLast4Digits} ${data.cardExpirationMonth}/${
            data.cardExpirationYear
          } added ${format(
            new Date(data.transactionCompletedDate),
            shortDateTimeFormat
          )}${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.NewDepositPaymentMethod: {
      const ccProcessor = data.ccProcessor
        ? `, Processor ${data.ccProcessor}`
        : '';

      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` used new method ${data.subTypeName}${ccProcessor}${managerName}`}
        </>
      );
    }
    case NOTIFICATION_TYPES.AppropriatenessTestPassed:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` ${
            data.isPassed ? 'passed' : 'failed'
          } the Appropriateness test${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.EconomicProfileChanged:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` filled out the Economic profile form${managerName}`}
        </>
      );
    case NOTIFICATION_TYPES.VideoVerificationRequested:
      return (
        <>
          <ClientIdAndName
            leadId={data.leadId}
            leadFullName={data.leadFullName}
          />
          {` with GUID ${data.leadGuid} is waiting for video verification.`}
        </>
      );
    case NOTIFICATION_TYPES.DormantClient:
      return (
        <>
          {`Client ID: `}
          <LeadGridFormatterTemplateWithInject leadId={data.leadId} />
          {` Dormant Fee Commission: ${String(
            data.dormantFeeCommission
          )}, Balance: ${getBalanceForDormantClientNotification(
            data.balance
          )}, Last trading Date: ${
            data.lastTradeDate
              ? format(new Date(data.lastTradeDate), shortDateTime)
              : ''
          }`}
        </>
      );
    case NOTIFICATION_TYPES.DormantClientRestoreActive:
      return (
        <>
          {`Client ID: `}
          <LeadGridFormatterTemplateWithInject leadId={data.leadId} />
          {` Balance: ${getBalanceForDormantClientNotification(
            data.balance
          )}, Last trading Date: ${
            data.lastTradeDate
              ? format(new Date(data.lastTradeDate), shortDateTime)
              : ''
          }`}
        </>
      );
    case NOTIFICATION_TYPES.RefillDemoAccountAttempt: {
      return (
        <>
          {`Client ID: `}
          <LeadGridFormatterTemplateWithInject leadId={data.leadId} /> was
          trying to refill his demo account.
        </>
      );
    }
    default:
      return <>Unknown notification event type {notificationType}</>;
  }
};
